import { z } from 'zod';

/**
 * Runtime environment for the fleet image — the one thing a static SPA cannot get from
 * `import.meta.env`.
 *
 * Vite inlines every `VITE_*` value at build time, so anything that has to differ between
 * deployments without a rebuild — the broker URL and the broker credentials — cannot travel
 * that way. The alternative used here is the standard one for a static app in a container:
 * the image's nginx entrypoint (`docker/40-signage-env.sh`) renders the container's
 * `SIGNAGE_MQTT_URL`, `SIGNAGE_MQTT_USERNAME` and `SIGNAGE_MQTT_PASSWORD` into
 * `/tmp/signage/env.json` at start-up, `nginx.conf` serves it as `/env.json`, and this
 * module fetches it once before anything else runs.
 *
 * In Kubernetes those three variables come from a Secret in the manifests repo
 * (`signage/live/broker-secret.yaml`), which makes the manifests the only place the broker
 * login exists — no GitHub secret, no rebuild to rotate. Locally, `docker run -e …` or the
 * `environment:` block in docker-compose does the same job.
 *
 * Only fetched when the build is `VITE_LOCAL_BROKER=runtime` (`@/lib/localBroker`). A display
 * or preview build never requests it, and a laptop local-broker build (`=true`) keeps reading
 * `.env`. Everything in the file is readable by any browser that opens the site — so was the
 * preventer API key — which is why the broker user is a scoped non-superuser.
 */

const runtimeEnvSchema = z.object({
  mqttUrl: z.string().optional(),
  mqttUsername: z.string().optional(),
  mqttPassword: z.string().optional(),
});

export type RuntimeEnv = z.infer<typeof runtimeEnvSchema>;

export const RUNTIME_ENV_URL = '/env.json';

let runtimeEnv: RuntimeEnv = {};

/**
 * Fetches and validates `/env.json`. Never throws: a missing or malformed file leaves the
 * env empty, and the caller (`LocalBrokerProvider`) says so on the console. That is the same
 * fail-loud-not-blank contract as `loadConfig` — a display with no credentials shows its
 * no-data state and a readable error, not a white screen.
 */
export async function loadRuntimeEnv(fetchFn: typeof fetch = fetch): Promise<RuntimeEnv> {
  let response: Response;
  try {
    response = await fetchFn(RUNTIME_ENV_URL, { cache: 'no-store' });
  } catch (error) {
    console.error('[runtime-env] %s could not be fetched:', RUNTIME_ENV_URL, error);
    return (runtimeEnv = {});
  }
  if (!response.ok) {
    console.error(
      '[runtime-env] %s returned %d. The image entrypoint did not render it — is this the ' +
        'live image, and is /tmp writable?',
      RUNTIME_ENV_URL,
      response.status,
    );
    return (runtimeEnv = {});
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch (error) {
    console.error('[runtime-env] %s is not valid JSON:', RUNTIME_ENV_URL, error);
    return (runtimeEnv = {});
  }

  const parsed = runtimeEnvSchema.safeParse(raw);
  if (!parsed.success) {
    console.error(
      '[runtime-env] %s has the wrong shape:',
      RUNTIME_ENV_URL,
      z.prettifyError(parsed.error),
    );
    return (runtimeEnv = {});
  }

  // Empty strings are what the entrypoint writes for an unset variable; treat them as absent
  // so every consumer has one check to make.
  const { mqttUrl, mqttUsername, mqttPassword } = parsed.data;
  runtimeEnv = {
    ...(mqttUrl ? { mqttUrl } : {}),
    ...(mqttUsername ? { mqttUsername } : {}),
    ...(mqttPassword ? { mqttPassword } : {}),
  };
  return runtimeEnv;
}

/** The last loaded env; `{}` until `loadRuntimeEnv` has resolved. */
export function getRuntimeEnv(): RuntimeEnv {
  return runtimeEnv;
}

/** Test seam. */
export function resetRuntimeEnv(): void {
  runtimeEnv = {};
}
